import React, { useCallback, useEffect, useRef } from 'react';
import {
  BlockType,
  getChildIdx,
  isListingPickerType,
  LISTING_PICKER_CATEGORY_BY_TYPE,
} from '@bay-email/core';
import { useHoverIdx } from '@/hooks/useHoverIdx';
import { useDataTransfer } from '@/hooks/useDataTransfer';
import { isUndefined } from 'lodash';
import { useBlock } from '@/hooks/useBlock';
import { useEditorProps } from '@/hooks/useEditorProps';
import { useListingPicker } from '@/hooks/useListingPicker';

export type BlockAvatarWrapperProps = {
  children?: React.ReactNode;
  type: BlockType | string;
  payload?: any;
  action?: 'add' | 'move';
  hideIcon?: boolean;
  idx?: string;
};

export const BlockAvatarWrapper: React.FC<BlockAvatarWrapperProps> = props => {
  const { type, children, payload, action = 'add', idx } = props;
  const { addBlock, moveBlock, values } = useBlock();
  const { setIsDragging, setHoverIdx } = useHoverIdx();
  const { setDataTransfer, dataTransfer } = useDataTransfer();
  const { blockMode, blockLibrary } = useEditorProps();
  const { open: openListingPicker } = useListingPicker();
  const ref = useRef<HTMLDivElement>(null);

  const onDragStart = useCallback(
    (ev: React.DragEvent) => {
      if (action === 'add') {
        setDataTransfer({
          type: type,
          action,
          payload,
        });
      } else {
        setDataTransfer({
          type: type,
          action,
          sourceIdx: idx,
        });
      }

      setIsDragging(true);
    },
    [action, idx, payload, setDataTransfer, setIsDragging, type],
  );

  const onDragEnd = useCallback(() => {
    setIsDragging(false);
    setHoverIdx('');
    if (!dataTransfer) return;
    if (action === 'add' && !isUndefined(dataTransfer.parentIdx)) {
      // A listing primitive (Single / Multiple Listings) never drops itself in
      // the main editor: it opens the Choose-a-Block picker filtered to its
      // category. The block the user picks is a wrapper/page-level design, so it
      // can't land where the primitive was dropped (a column) — it goes at the
      // page root. Resolve that root position from the drop NOW, while the drop
      // target is known: the top-level block the drop was inside, then prepend or
      // append by the drop indicator's direction. The block editor has no
      // library, so it keeps the plain insert. The picker also only exists when
      // the host wired a block library (email_block_editor flag on) — with no
      // library, the listing primitive inserts directly.
      if (!blockMode && isListingPickerType(type) && Boolean(blockLibrary?.fetchByCategory)) {
        const PAGE_IDX = 'content';
        let positionIndex: number | undefined;
        if (dataTransfer.parentIdx === PAGE_IDX) {
          // Dropped directly at the root gap — index is already root-level.
          positionIndex = dataTransfer.positionIndex;
        } else {
          const rootMatch = /^content\.children\.\[(\d+)\]/.exec(
            dataTransfer.parentIdx || '',
          );
          if (rootMatch) {
            const append =
              dataTransfer.endDirection === 'bottom' ||
              dataTransfer.endDirection === 'right';
            positionIndex = Number(rootMatch[1]) + (append ? 1 : 0);
          }
        }
        openListingPicker({
          type,
          category: LISTING_PICKER_CATEGORY_BY_TYPE[type],
          parentIdx: PAGE_IDX,
          positionIndex,
        });
        return;
      }
      addBlock({
        type,
        parentIdx: dataTransfer.parentIdx,
        positionIndex: dataTransfer.positionIndex,
        payload,
      });
    } else {
      if (
        idx &&
        !isUndefined(dataTransfer.sourceIdx) &&
        !isUndefined(dataTransfer.parentIdx) &&
        !isUndefined(dataTransfer.positionIndex)
      ) {
        moveBlock(
          dataTransfer.sourceIdx,
          getChildIdx(dataTransfer.parentIdx, dataTransfer.positionIndex),
        );
      }
    }
  }, [
    action,
    addBlock,
    blockMode,
    blockLibrary,
    idx,
    moveBlock,
    dataTransfer,
    openListingPicker,
    payload,
    setHoverIdx,
    setIsDragging,
    type,
  ]);

  useEffect(() => {
    const ele = ref.current;
    if (!ele) return;

    ele.addEventListener('dragend', onDragEnd);
    return () => {
      ele.removeEventListener('dragend', onDragEnd);
    };
  }, [onDragEnd]);

  return (
    <div
      style={{ cursor: 'grab' }}
      ref={ref}
      onMouseDown={() => {
        window.getSelection()?.removeAllRanges();
      }}
      data-type={type}
      onDragStart={onDragStart}
      draggable
    >
      {children}
    </div>
  );
};
